import React from 'react';
import { Paper, Stack, TextField, Autocomplete, Chip } from '@mui/material';
import { Controller } from 'react-hook-form';
import type { FieldErrors, UseFormRegister, Control } from 'react-hook-form';
import type { UpdateProfileDetails } from '@/schemas/updateProfileSchema';
import { USER_PREFERENCES } from '@/constants/membership.constants';

const { CITIES, POSITIONS, INDUSTRIES, INTERESTS } = USER_PREFERENCES;

type PersonalInfoFields =
  | 'firstName'
  | 'lastName'
  | 'mobile'
  | 'company'
  | 'city'
  | 'position'
  | 'industry'
  | 'website'
  | 'twitter'
  | 'instagram'
  | 'interests';

type Props = {
  userDetails?: UserDetails;
  register?: UseFormRegister<UpdateProfileDetails>;
  errors?: FieldErrors<UpdateProfileDetails>;
  control?: Control<UpdateProfileDetails>;
  showPersonalInfo: boolean | PersonalInfoFields[];
  hideTitle?: boolean;
};

export const PersonalInfoSection: React.FC<Props> = ({
  userDetails,
  register,
  errors,
  control,
  showPersonalInfo
}) => {
  const allFields: PersonalInfoFields[] = [
    'firstName',
    'lastName',
    'mobile',
    'company',
    'city',
    'position',
    'industry',
    'website',
    'twitter',
    'instagram',
    'interests'
  ];
  const showAll = showPersonalInfo === true;
  const showSet = showAll
    ? new Set(allFields)
    : new Set(Array.isArray(showPersonalInfo) ? showPersonalInfo : []);

  return (
    <>
      {control && errors && register && (
        <Stack spacing={2.5}>
          {(showSet.has('firstName') || showSet.has('lastName')) && (
            <Stack direction='row' spacing={2}>
              {showSet.has('firstName') && (
                <TextField
                  label='First Name'
                  {...register('firstName')}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  sx={{ flex: 1 }}
                />
              )}
              {showSet.has('lastName') && (
                <TextField
                  label='Last Name'
                  {...register('lastName')}
                  defaultValue={userDetails?.lastName}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
          )}
          {showSet.has('mobile') && (
            <TextField
              label='Mobile'
              {...register('mobile')}
              defaultValue={userDetails?.mobile}
              error={!!errors.mobile}
              helperText={errors.mobile?.message}
            />
          )}
          {showSet.has('city') && (
            <Controller
              control={control}
              name='city'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  value={CITIES.find((city) => city.value === value) ?? null}
                  onChange={(_, newValue) => {
                    onChange(newValue?.value);
                  }}
                  options={CITIES}
                  renderInput={(params) => (
                    <TextField
                      label='City'
                      {...params}
                      error={!!errors.city}
                      helperText={errors.city?.message}
                    />
                  )}
                />
              )}
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {showSet.has('company') && (
              <TextField
                label='Company'
                {...register('company')}
                error={!!errors.company}
                helperText={errors.company?.message}
                sx={{ flex: 1 }}
              />
            )}
            {showSet.has('position') && (
              <Controller
                control={control}
                name='position'
                render={({ field: { onChange, value } }) => (
                  <Autocomplete
                    value={POSITIONS.find((pos) => pos.value === value) ?? null}
                    onChange={(_, newValue) => {
                      onChange(newValue?.value);
                    }}
                    options={POSITIONS}
                    sx={{ flex: 1 }}
                    renderInput={(params) => (
                      <TextField
                        label='Position'
                        {...params}
                        {...register('position')}
                        error={!!errors.position}
                        helperText={errors.position?.message}
                        SelectProps={{
                          MenuProps: { style: { maxHeight: 400 } }
                        }}
                      />
                    )}
                  />
                )}
              />
            )}
          </Stack>
          {showSet.has('industry') && (
            <Controller
              control={control}
              name='industry'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  value={INDUSTRIES.find((ind) => ind.value === value) ?? null}
                  onChange={(_, newValue) => {
                    onChange(newValue?.value);
                  }}
                  options={INDUSTRIES}
                  renderInput={(params) => (
                    <TextField
                      label='Industry'
                      {...params}
                      {...register('industry')}
                      error={!!errors.industry}
                      helperText={errors.industry?.message}
                      SelectProps={{ MenuProps: { style: { maxHeight: 400 } } }}
                    />
                  )}
                />
              )}
            />
          )}
          {showSet.has('interests') && (
            <Controller
              control={control}
              name='interests'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  value={
                    INTERESTS.filter((int) => value?.includes(int.value)) ?? []
                  }
                  onChange={(_, newValue) => {
                    onChange([...newValue.map((val) => val.value)]);
                  }}
                  disableCloseOnSelect
                  multiple
                  options={INTERESTS}
                  renderTags={(value) => {
                    return value.map((option) => {
                      return (
                        <Chip
                          variant='outlined'
                          sx={{ margin: '2px 2px' }}
                          color='secondary'
                          key={option.value}
                          label={
                            INTERESTS.find((int) => int.value === option.value)
                              ?.label
                          }
                        />
                      );
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      label={
                        value && value.length > 0
                          ? 'Interests'
                          : "Industry sectors you're focused on"
                      }
                      {...params}
                      SelectProps={{
                        multiple: true,
                        MenuProps: { style: { maxHeight: 400 } }
                      }}
                    />
                  )}
                />
              )}
            />
          )}
          {showSet.has('website') && (
            <TextField label='Website' {...register('website')} />
          )}
          {showSet.has('twitter') && (
            <TextField label='Twitter' {...register('twitter')} />
          )}
          {showSet.has('instagram') && (
            <TextField label='Instagram' {...register('instagram')} />
          )}
        </Stack>
      )}
    </>
  );
};
